import { useEffect, useMemo, useState } from "react";
import { Download, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { fmtDate } from "@/lib/format";

function toCSV(rows) {
  if (!rows.length) return "";
  const headers = ["full_name", "date_of_birth", "phone", "marital_status", "anniversary", "language", "created_at"];
  const lines = [headers.join(",")];
  for (const r of rows) {
    lines.push(headers.map((h) => `"${(r[h] ?? "").toString().replace(/"/g, '""')}"`).join(","));
  }
  return lines.join("\n");
}

export default function AdminMembers() {
  const { t, lang } = useI18n();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [pending, setPending] = useState(null); // member to delete
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try { const { data } = await api.get("/admin/members"); setList(data); } catch (_) {}
  };
  useEffect(() => { reload(); }, []);

  const filtered = useMemo(() => {
    if (!q) return list;
    const lq = q.toLowerCase();
    return list.filter((m) => (m.full_name || "").toLowerCase().includes(lq) || (m.phone || "").includes(q));
  }, [list, q]);

  const onExport = () => {
    const csv = toCSV(filtered);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `miembros-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const doDelete = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      await api.delete(`/admin/members/${pending.id}`);
      toast.success(t.admin.deleted);
      setPending(null);
      reload();
    } catch (_) { toast.error(t.common.error); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="font-serif-display text-2xl font-semibold">{t.admin.members}</h1>
        <Button variant="outline" onClick={onExport} data-testid="admin-members-export"><Download className="mr-1 h-4 w-4" />{t.admin.exportCsv}</Button>
      </div>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-11" placeholder={lang === "es" ? "Buscar..." : "Search..."} data-testid="admin-table-search-input" />
      </div>

      <Card className="rounded-2xl ring-1 ring-black/5 overflow-hidden">
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground" data-testid="admin-members-empty">{t.admin.noItems}</p>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="admin-members-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>{lang === "es" ? "Nombre" : "Name"}</TableHead>
                    <TableHead>DOB</TableHead>
                    <TableHead>{t.register.phone}</TableHead>
                    <TableHead>{t.register.maritalStatus}</TableHead>
                    <TableHead>{t.register.anniversary}</TableHead>
                    <TableHead>{lang === "es" ? "Registrado" : "Registered"}</TableHead>
                    <TableHead className="text-right">&nbsp;</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell className="font-medium">{m.full_name}</TableCell>
                      <TableCell>{fmtDate(m.date_of_birth, lang)}</TableCell>
                      <TableCell>{m.phone}</TableCell>
                      <TableCell>{t.register.ms[m.marital_status] || m.marital_status}</TableCell>
                      <TableCell>{m.anniversary ? fmtDate(m.anniversary, lang) : "\u2014"}</TableCell>
                      <TableCell>{fmtDate(m.created_at, lang)}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => setPending(m)} aria-label="delete" data-testid={`admin-member-delete-${m.id}`}>
                          <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <AlertDialogContent data-testid="admin-members-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{lang === "es" ? "Eliminar miembro" : "Delete member"}</AlertDialogTitle>
            <AlertDialogDescription>
              {lang === "es"
                ? `Esta accion eliminara permanentemente a "${pending?.full_name || ""}" del registro. No se puede deshacer.`
                : `This will permanently delete "${pending?.full_name || ""}" from the member list. This cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t.common.cancel}</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} disabled={busy} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="admin-members-delete-confirm">
              {busy ? "..." : (lang === "es" ? "Eliminar" : "Delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
